/**
 * backtestDebugInfo.js
 *
 * Utility for formatting backtest data into AI-friendly debug information.
 * Used by the debug copy button in HistoryCard and BacktestStatusCard.
 *
 * WHY THIS EXISTS:
 * - Users need to share backtest details with AI tools for debugging
 * - Manually copying backtest info is tedious and error-prone
 * - This utility formats data in a consistent, AI-readable markdown format
 * - Excludes sensitive data (API keys, credentials) for security
 *
 * USAGE:
 * - formatBacktestDebugInfo(backtest) - Returns formatted markdown string
 * - copyBacktestDebugInfo(backtest) - Copies to clipboard, returns the text
 */

/**
 * Format a date value for display, handling various input formats
 * @param {string|Date|number} dateValue - Date to format
 * @param {string} fallback - Fallback text when date is missing (default: 'Not found')
 * @returns {string} Formatted date string or fallback
 */
function formatDate(dateValue, fallback = 'Not found') {
  if (!dateValue) return fallback;
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return fallback;
    return date.toLocaleString();
  } catch {
    return fallback;
  }
}

/**
 * Safely get a nested value from an object
 * @param {Object} obj - Object to get value from
 * @param {string} path - Dot-separated path (e.g., 'error_details.message')
 * @param {*} defaultValue - Default value if path not found
 * @returns {*} The value or default
 */
function safeGet(obj, path, defaultValue = 'Not found') {
  if (!obj || !path) return defaultValue;
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return defaultValue;
    current = current[key];
  }
  return current ?? defaultValue;
}

export function getBacktestErrorMessage(backtest, fallback = null) {
  const rawDetails = backtest?.error_details ?? backtest?.errorDetails ?? null;
  if (typeof rawDetails === 'string' && rawDetails.trim()) {
    return rawDetails.trim();
  }
  if (rawDetails && typeof rawDetails === 'object') {
    const nestedMessage = rawDetails.message ?? rawDetails.error ?? rawDetails.detail;
    if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
      return nestedMessage.trim();
    }
  }
  if (typeof backtest?.error === 'string' && backtest.error.trim()) {
    return backtest.error.trim();
  }
  return fallback;
}

/**
 * Format backtest data into AI-friendly debug information.
 * Excludes sensitive data (API keys, credentials) for security.
 *
 * @param {Object} backtest - The backtest object with status, results, error details, etc.
 * @returns {string} Formatted markdown string ready for AI tools
 */
export function formatBacktestDebugInfo(backtest) {
  if (!backtest) {
    return '## Backtest Debug Information\n\nNo backtest data available.';
  }

  // Get strategy ID from various possible sources
  // Priority: direct strategyId > aiStrategyId > revision.aiStrategy.id
  const strategyId = backtest.strategyId
    || backtest.strategy_id
    || backtest.aiStrategyId
    || backtest.ai_strategy_id
    || safeGet(backtest, 'revision.aiStrategy.id', null)
    || safeGet(backtest, 'revision.aiStrategyId', null);

  const lines = [
    '## Backtest Debug Information',
    '',
    '### Basic Info',
    `- **Backtest ID:** ${backtest.id || backtest.backtestId || 'Not found'}`,
    `- **Status:** ${backtest.status || 'Unknown'}`,
    `- **Strategy ID:** ${strategyId || 'Not found'}`,
    `- **Revision ID:** ${backtest.revisionId || safeGet(backtest, 'revision.id', null) || 'Not found'}`,
    `- **AI Strategy ID:** ${backtest.aiStrategyId || backtest.ai_strategy_id || 'Not found'}`,
    ''
  ];

  // Time information
  // Note: The backtest entity only tracks createdAt and durationMs, not startedAt/completedAt
  lines.push('### Timing');
  lines.push(`- **Created:** ${formatDate(backtest.createdAt || backtest.created_at)}`);

  // Check if startedAt/completedAt are available (they may not be tracked)
  const startedAt = backtest.startedAt || backtest.started_at;
  const completedAt = backtest.completedAt || backtest.completed_at;
  lines.push(`- **Started:** ${formatDate(startedAt, 'Not tracked')}`);
  lines.push(`- **Completed:** ${formatDate(completedAt, 'Not tracked')}`);

  // Duration - prefer direct durationMs field, otherwise calculate from timestamps
  const durationMs = backtest.durationMs || backtest.duration_ms;
  if (durationMs) {
    const durationSec = Math.round(durationMs / 1000);
    lines.push(`- **Duration:** ${durationSec}s`);
  } else if (startedAt && completedAt) {
    const calculatedDuration = new Date(completedAt) - new Date(startedAt);
    const durationSec = Math.round(calculatedDuration / 1000);
    lines.push(`- **Duration:** ${durationSec}s`);
  }
  lines.push('');

  // Backtest parameters
  if (backtest.startDate || backtest.endDate || backtest.initialCash) {
    lines.push('### Backtest Parameters');
    if (backtest.startDate) lines.push(`- **Start Date:** ${backtest.startDate}`);
    if (backtest.endDate) lines.push(`- **End Date:** ${backtest.endDate}`);
    if (backtest.initialCash) lines.push(`- **Initial Cash:** $${Number(backtest.initialCash).toLocaleString()}`);
    if (backtest.symbol) lines.push(`- **Symbol:** ${backtest.symbol}`);
    if (backtest.asset) lines.push(`- **Asset:** ${backtest.asset}`);
    lines.push('');
  }

  // Performance metrics (for completed backtests)
  const results = backtest.results || backtest.result || {};
  if (backtest.status === 'completed' && Object.keys(results).length > 0) {
    lines.push('### Performance Results');

    // Common metrics
    const metrics = [
      ['Total Return', results.total_return || results.totalReturn],
      ['CAGR', results.cagr || results.CAGR],
      ['Max Drawdown', results.max_drawdown || results.maxDrawdown],
      ['Sharpe Ratio', results.sharpe_ratio || results.sharpeRatio],
      ['Sortino Ratio', results.sortino_ratio || results.sortinoRatio],
      ['Win Rate', results.win_rate || results.winRate],
      ['Total Trades', results.total_trades || results.totalTrades],
      ['Volatility', results.volatility]
    ];

    for (const [name, value] of metrics) {
      if (value !== undefined && value !== null) {
        // Format percentages and numbers appropriately
        let formatted = value;
        if (typeof value === 'number') {
          if (name.includes('Ratio') || name === 'Volatility') {
            formatted = value.toFixed(2);
          } else if (name.includes('Return') || name.includes('Drawdown') || name.includes('Rate') || name === 'CAGR') {
            formatted = `${(value * 100).toFixed(2)}%`;
          }
        }
        lines.push(`- **${name}:** ${formatted}`);
      }
    }
    lines.push('');
  }

  // Error details (for failed backtests)
  if (backtest.status === 'failed' || backtest.error_details || backtest.errorDetails || backtest.error) {
    lines.push('### Error Details');

    const rawErrorDetails = backtest.error_details ?? backtest.errorDetails ?? {};
    const errorDetails = rawErrorDetails && typeof rawErrorDetails === 'object'
      ? rawErrorDetails
      : {};
    const errorMessage = getBacktestErrorMessage(backtest, 'Unknown error');

    lines.push(`- **Error Message:** ${errorMessage}`);

    if (errorDetails.error_type || errorDetails.errorType) {
      lines.push(`- **Error Type:** ${errorDetails.error_type || errorDetails.errorType}`);
    }

    if (errorDetails.stage) {
      lines.push(`- **Stage:** ${errorDetails.stage}`);
    }

    if (errorDetails.line_number || errorDetails.lineNumber) {
      lines.push(`- **Line Number:** ${errorDetails.line_number || errorDetails.lineNumber}`);
    }

    // Missing environment variables
    if (errorDetails.missing_env_vars || errorDetails.missingEnvVars) {
      const missingVars = errorDetails.missing_env_vars || errorDetails.missingEnvVars;
      const varsList = Array.isArray(missingVars) ? missingVars.join(', ') : missingVars;
      lines.push(`- **Missing Env Vars:** ${varsList}`);
    }

    // Stack trace (truncated for readability)
    if (errorDetails.stack_trace || errorDetails.stackTrace) {
      const stack = errorDetails.stack_trace || errorDetails.stackTrace;
      const truncatedStack = typeof stack === 'string' && stack.length > 500
        ? stack.substring(0, 500) + '...(truncated)'
        : stack;
      lines.push('');
      lines.push('**Stack Trace:**');
      lines.push('```');
      lines.push(truncatedStack);
      lines.push('```');
    }

    lines.push('');
  }

  // Progress info (for running backtests)
  if (backtest.status === 'running' || backtest.status === 'pending') {
    lines.push('### Progress');
    if (backtest.progress !== undefined) {
      lines.push(`- **Progress:** ${Math.round(backtest.progress * 100)}%`);
    }
    if (backtest.currentDate) {
      lines.push(`- **Current Date:** ${backtest.currentDate}`);
    }
    if (backtest.step || backtest.currentStep) {
      lines.push(`- **Current Step:** ${backtest.step || backtest.currentStep}`);
    }
    lines.push('');
  }

  // Logs URL (if available)
  if (backtest.logsUrl || backtest.logs_url) {
    lines.push('### Additional Resources');
    lines.push(`- **Logs URL:** ${backtest.logsUrl || backtest.logs_url}`);
    lines.push('');
  }

  // Add footer with timestamp
  lines.push('---');
  lines.push(`*Debug info generated at ${new Date().toISOString()}*`);

  return lines.join('\n');
}

/**
 * Copy backtest debug info to clipboard.
 *
 * @param {Object} backtest - The backtest object
 * @returns {Promise<string>} The formatted text that was copied
 * @throws {Error} If clipboard access fails
 */
export async function copyBacktestDebugInfo(backtest) {
  const text = formatBacktestDebugInfo(backtest);

  try {
    await navigator.clipboard.writeText(text);
    return text;
  } catch (err) {
    // Fallback for browsers that don't support clipboard API
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return text;
    } catch (fallbackErr) {
      document.body.removeChild(textArea);
      throw new Error('Failed to copy to clipboard');
    }
  }
}

const backtestDebugInfo = { formatBacktestDebugInfo, copyBacktestDebugInfo };

export default backtestDebugInfo;
